import React, { useState, useEffect } from 'react';
import { UserPlus, UserMinus } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGetJson } from '../../utils/api';
import { useTheme } from '../../context/ThemeContext';
import { useAssignServiceRequest, useUnassignServiceRequest } from './serviceRequestHooks';

export default function AssignmentControls({ publicId, currentAssigneeName, departmentSlugs = [] }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState('');
  const [open, setOpen] = useState(false);
  const assign = useAssignServiceRequest(publicId);
  const unassign = useUnassignServiceRequest(publicId);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        // Admin endpoint that lists active users — fallback to a basic list if unavailable.
        const data = await apiGetJson('/admin/users?per_page=50');
        const list = data?.users || [];
        setUsers(list);
      } catch {
        setUsers([]);
      }
    };
    load();
  }, [open]);

  const submit = async () => {
    if (!selected) return;
    try {
      await assign.mutateAsync({ user_id: selected });
      toast.success('Assigned.');
      setOpen(false);
      setSelected('');
    } catch (e) {
      console.error(e);
      toast.error('Could not assign this request.');
    }
  };

  const remove = async () => {
    if (!confirm('Remove the current assignee?')) return;
    try {
      await unassign.mutateAsync();
      toast.success('Unassigned.');
    } catch (e) {
      console.error(e);
      toast.error('Could not unassign.');
    }
  };

  if (currentAssigneeName) {
    return (
      <div className="flex items-center gap-2 text-sm font-rasa">
        <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
          Assigned to <strong>{currentAssigneeName}</strong>
        </span>
        <button type="button" onClick={remove}
                className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-600">
          <UserMinus size={12} /> Unassign
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md font-rasa bg-blue-500 text-white hover:bg-blue-600"
        >
          <UserPlus size={14} /> Assign
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className={`text-sm font-rasa rounded-md border px-2 py-1.5 ${
              isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-300'
            }`}
          >
            <option value="">Pick an assignee…</option>
            {users.map((u) => (
              <option key={u.public_id || u.id} value={u.public_id}>
                {u.full_name || u.username || u.email} ({u.role})
              </option>
            ))}
          </select>
          <button type="button" onClick={submit} disabled={!selected || assign.isPending}
                  className="text-sm px-3 py-1.5 rounded-md font-rasa bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50">
            Assign
          </button>
          <button type="button" onClick={() => setOpen(false)}
                  className={`text-sm px-2 py-1.5 rounded-md font-rasa ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
